import { FIELD_LABEL, FIELD_READONLY } from "@/components/fieldStyles";
import type { NumericKind } from "@/components/NumericField";
import { NOT_PROVIDED } from "@/lib/display";
import { numberBox } from "@/lib/displayFormat";

// BRK-S349 — a value that can't be edited, shown the way the form shows the
// field that holds it (Nav, 2026-10-13): the label above, the value in a box
// the control's size, with the unit the field draws inside it. Where the
// account form shows a read-only value in its own input, this is text, so
// nothing reads as a box to type into: a <dt>/<dd> pair in the caller's <dl>.

export function ReadOnlyField({
  label,
  value,
  start,
  end,
  full,
  empty = NOT_PROVIDED,
}: {
  label: string;
  /** As its field shows it. Missing or blank reads as `empty`. */
  value: string | null | undefined;
  /** The unit its field draws before the value, as `$`. */
  start?: string;
  /** The unit after it, as `%`. */
  end?: string;
  /** Spans every column of its grid, as its field does on its step. */
  full?: boolean;
  /** What a missing value reads as. */
  empty?: string;
}) {
  const shown = typeof value === "string" && value.trim() !== "";
  return (
    <div
      className={`flex min-w-0 flex-col gap-1${full ? " col-span-full" : ""}`}
    >
      <dt className={FIELD_LABEL}>{label}</dt>
      <dd className={FIELD_READONLY}>
        {shown && start && (
          <span className="shrink-0 text-brk-dim">{start}</span>
        )}
        <span className={`min-w-0 break-words${shown ? "" : " text-brk-dim"}`}>
          {shown ? value : empty}
        </span>
        {shown && end && <span className="shrink-0 text-brk-dim">{end}</span>}
      </dd>
    </div>
  );
}

/** A number as `NumericField` shows it, in a read-only box. */
export function ReadOnlyNumber({
  kind,
  label,
  value,
  full,
}: {
  kind: NumericKind;
  label: string;
  /** `undefined` when nobody gave one; 0 is a value. */
  value: number | undefined;
  full?: boolean;
}) {
  return (
    <ReadOnlyField label={label} {...numberBox(kind, value)} full={full} />
  );
}
